import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, ImagePlus, Loader2, Send, CheckCircle2, X } from "lucide-react";
import { Reveal } from "./Reveal";
import { API } from "@/lib/site";

const inputCls =
  "w-full rounded-xl border border-emerald-900/15 bg-white px-4 py-3.5 text-sm text-slate-900 outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-slate-400 focus:border-[#2E8B57] focus:ring-4 focus:ring-[#2E8B57]/15";

const StarPicker = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Your rating" data-testid="review-star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          data-testid={`review-star-${n}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          className="rounded-md p-0.5 transition-transform duration-200 hover:scale-110"
        >
          <Star className={`h-7 w-7 transition-colors ${n <= shown ? "fill-[#D97706] stroke-[#D97706]" : "fill-none stroke-slate-300"}`} />
        </button>
      ))}
      <span className="ml-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {["", "Poor", "Fair", "Good", "Very good", "Excellent"][shown]}
      </span>
    </div>
  );
};

export const ReviewForm = () => {
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) {
      toast.error("Please write at least a few words about your experience.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("rating", String(rating));
      fd.append("text", text.trim());
      if (photo) fd.append("photo", photo);
      await axios.post(`${API}/reviews`, fd);
      setDone(true);
      toast.success("Thank you! Your review has been sent for approval.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your review. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Reveal>
      <div
        id="write-review"
        data-testid="review-form-card"
        className="mt-16 overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-[0_30px_60px_-30px_rgba(30,58,138,0.25)] lg:grid lg:grid-cols-5"
      >
        <div className="bg-[#0F2440] p-8 text-white sm:p-10 lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-300">Share your experience</p>
          <h3 className="font-heading mt-4 text-2xl font-semibold leading-tight sm:text-3xl">Practised with us? Tell your story.</h3>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            Your words help someone in the islands take their first step towards healing. Every review is read personally by Dr. Rajni Ahuja and published after a quick check.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-slate-200">
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-300" /> Takes under a minute</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-300" /> Photo is optional</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-300" /> Published after approval</li>
          </ul>
        </div>

        <div className="p-8 sm:p-10 lg:col-span-3">
          {done ? (
            <div data-testid="review-success-state" className="flex h-full flex-col items-start justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#2E8B57]/10 text-[#2E8B57]">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h4 className="font-heading mt-5 text-2xl font-semibold text-slate-900">Namaste, {name.trim() || "friend"} — thank you!</h4>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                Your review has reached us and will appear on this page once Dr. Rajni approves it.
              </p>
              <button
                data-testid="review-write-another-button"
                onClick={() => { setDone(false); setName(""); setText(""); setRating(5); setPhoto(null); }}
                className="mt-6 text-sm font-semibold text-[#2E8B57] underline-offset-4 hover:underline"
              >
                Write another review
              </button>
            </div>
          ) : (
            <form data-testid="review-form" onSubmit={submit} className="space-y-5">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Your rating</label>
                <StarPicker value={rating} onChange={setRating} />
              </div>
              <div>
                <label htmlFor="review-name" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Your name</label>
                <input id="review-name" data-testid="review-name-input" required minLength={2} maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya Sharma" className={inputCls} />
              </div>
              <div>
                <label htmlFor="review-text" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Your experience</label>
                <textarea id="review-text" data-testid="review-text-input" required minLength={10} maxLength={1200} rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="What changed for you after joining NAVYOGA? Which therapy or batch did you attend?" className={`${inputCls} resize-none`} />
                <p className="mt-1.5 text-right text-[11px] text-slate-400">{text.length}/1200</p>
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Add a photo (optional)</label>
                <input ref={fileRef} data-testid="review-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                {photo ? (
                  <div data-testid="review-photo-preview" className="flex items-center gap-3 rounded-xl border border-emerald-900/15 bg-[#F8FFF8] p-2 pr-3">
                    <img src={URL.createObjectURL(photo)} alt="Selected" className="h-14 w-14 rounded-lg object-cover" />
                    <p className="min-w-0 flex-1 truncate text-sm text-slate-700">{photo.name}</p>
                    <button type="button" data-testid="review-photo-remove-button" onClick={() => { setPhoto(null); if (fileRef.current) fileRef.current.value = ""; }} className="rounded-full p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-500" aria-label="Remove photo">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button type="button" data-testid="review-photo-choose-button" onClick={() => fileRef.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-emerald-900/15 bg-[#F8FFF8] px-4 py-4 text-sm font-medium text-slate-500 transition-colors hover:border-[#2E8B57]/50 hover:text-[#2E8B57]">
                    <ImagePlus className="h-4 w-4" />
                    Choose a photo (max 10 MB)
                  </button>
                )}
              </div>
              <button
                data-testid="review-submit-button"
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 rounded-full bg-[#2E8B57] px-7 py-3.5 text-sm font-semibold text-white transition-[transform,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:bg-[#267a4b] disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending…" : "Submit Review"}
              </button>
            </form>
          )}
        </div>
      </div>
    </Reveal>
  );
};
